import { createSlice } from "@reduxjs/toolkit";
import { getThemeFromLocalStorage } from "../Saga/localStorage";

const dataSlice = createSlice({
  name: "data",
  initialState: {
    data: [],
    contentSuggestions: [], // Sugestie dla zakładki "Treści"
    commentSuggestions: [], // Sugestie dla zakładki "Komentarze"
    themedSuggestions: [], // Nowy stan dla zakładki "Posty tematyczne"
    contentResponseType: "suggestions", // Typ odpowiedzi dla treści
    commentResponseType: "suggestions", // Typ odpowiedzi dla komentarzy
    themedResponseType: "suggestions", // Nowy typ odpowiedzi dla postów tematycznych
    imageUrl: null,
    isLoading: "",
    suggestionsLoading: false,
    suggestionsError: null,
    lightMode: getThemeFromLocalStorage(),
    polishMode: true,
    routeName: "design",
    selectedSuggestion: null,
    suggestionDetails: null,
    detailsLoading: false,
    documentGenerationError: null,
    responseType: "suggestions",
    videoFileId: null,
    videoUrl: null,
    videoProgress: null,
    videoTimerActive: false,
    videoTimerCompleted: false,
    chatbotId: null,
    chatHistory: [],
    chatHistoryVersion: 0,
    tilesDetails: [],
    user: null,
    openAiTokens: 0,
    restrictedDataLoading: false,
    restrictedDataError: null,
    copiedPost: null,
    musicTaskId: null,
    musicUrl: null,
    musicLoading: false,
    musicError: null,
    musicTitle: null,
    musicProgress: null,
    musicResult: null,
    musicProgressTimer: null,
    musicTimerActive: false,
    musicTimerCompleted: false,
    musicChatbotId: null,
    dietPlanContext: null,
    activeSubscription: null,
    premiumGranted: false,
    generationCounts: null,
  },
  reducers: {
    fetchActiveSubscription: (state) => {
      state.isLoading = "loading";
    },
    setActiveSubscription: (state, { payload }) => {
      state.activeSubscription = payload;
      state.isLoading = "success";
    },
    fetchData: (state) => {
      state.isLoading = "loading";
    },
    setData: (state, { payload }) => {
      state.data = payload;
      state.isLoading = "success";
    },
    fetchSuggestions: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
    },
    setSuggestions: (state, { payload }) => {
      state.data = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
      state.responseType = Array.isArray(payload) ? "suggestions" : "message";
    },
    setLegalAdvice: (state, { payload }) => {
      // payload to teraz obiekt { content, remainingTokens }
      state.data = payload.content;
      state.openAiTokens = payload.remainingTokens;
      state.suggestionsLoading = false;
      state.responseType = typeof payload.content === "string" ? "message" : "suggestions";
    },
    setGeneratedDocument: (state, { payload }) => {
    // payload to teraz obiekt { content, remainingTokens }
    state.suggestionDetails = payload.content; // Aktualizuje treść dokumentu
    state.openAiTokens = payload.remainingTokens; // Aktualizuje tokeny
    state.detailsLoading = false;
    state.documentGenerationError = null;
},
    setError: (state, { payload = {} }) => {
      const { context, message } = payload;
      if (context === "suggestions") {
        state.suggestionsLoading = false;
        state.suggestionsError = message || "Wystąpił błąd podczas ładowania sugestii.";
      } else if (context === "restrictedData") {
        state.restrictedDataLoading = false;
        state.restrictedDataError = message || "Wystąpił błąd podczas ładowania danych.";
      } else if (context === "video") {
        state.suggestionsLoading = false;
        state.suggestionsError = message || "Wystąpił błąd podczas generowania wideo.";
        state.videoTaskId = null;
      } else {
        state.isLoading = "error";
        state.suggestionsLoading = false;
        state.suggestionsError = message || "Wystąpił błąd podczas ładowania danych.";
      }
    },
    setContentSuggestions: (state, { payload }) => {
      state.contentSuggestions = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
      state.contentResponseType = Array.isArray(payload) ? "suggestions" : "message";
    },
    setCommentSuggestions: (state, { payload }) => {
      state.commentSuggestions = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
      state.commentResponseType = Array.isArray(payload) ? "suggestions" : "message";
    },
    setThemedSuggestions: (state, { payload }) => {
      state.themedSuggestions = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
      state.themedResponseType = Array.isArray(payload) ? "suggestions" : "message";
    },
    clearSuggestions: (state) => {
      state.data = [];
      state.suggestionsError = null;
      state.dietPlanContext = null;
    },
    changeThemeAfterDelay: (state) => {
      state.lightMode = !state.lightMode;
    },
    togglePolishMode: (state) => {
      state.polishMode = !state.polishMode;
    },
    toggleBodyOverflow: (state) => {
      state.isBodyOverflowHidden = !state.isBodyOverflowHidden;
    },
    toggleBodyOverflowToFalse: (state) => {
      state.isBodyOverflowHidden = false;
    },
    setRouteName: (state, { payload }) => {
      state.routeName = payload;
    },
    setTimeOfDay: (state, { payload: isDayTime }) => {
      state.isNoon = isDayTime;
    },
    fetchSuggestionDetails: (state, { payload }) => {
      state.selectedSuggestion = typeof payload === 'object' ? payload.index : payload;
      state.detailsLoading = true;
    },
    startDocumentGeneration: (state) => {
      state.detailsLoading = true;
      state.documentGenerationError = null;
    },
    setSuggestionDetails: (state, { payload }) => {
      state.suggestionDetails = payload;
      state.detailsLoading = false;
      state.documentGenerationError = null;
    },
    setDocumentError: (state, { payload }) => {
      state.detailsLoading = false;
      state.suggestionDetails = null;
      state.documentGenerationError = payload || "Wystąpił błąd podczas generowania dokumentu.";
    },
    clearSuggestionDetails: (state) => {
      state.selectedSuggestion = null;
      state.suggestionDetails = null;
      state.documentGenerationError = null;
    },
    resetState: (state) => {
      state.suggestionsLoading = false;
      state.suggestionsError = null;
      state.data = [];
      state.contentSuggestions = [];
      state.commentSuggestions = [];
      state.themedSuggestions = [];
      state.imageUrl = null;
      state.documentGenerationError = null;
      state.videoFileId = null;
      state.videoUrl = null;
      state.copiedPost = null;
      state.responseType = "suggestions";
    },
    setChatbotId: (state, { payload }) => {
      state.chatbotId = payload;
    },
    saveChatHistory: (state, { payload }) => {
      // Pusto – zapis przenosimy do sagi
    },
    setChatHistory: (state, { payload }) => {
      state.chatHistory = payload;
    },
    updateChatHistoryEntry: (state, { payload }) => {
      const index = state.chatHistory.findIndex(entry => entry.id === payload.id);
      if (index !== -1) {
        state.chatHistory[index] = { ...state.chatHistory[index], ...payload };
      }
    },
    incrementChatHistoryVersion: (state) => {
      state.chatHistoryVersion += 1;
    },
    setUser: (state, { payload }) => {
      state.user = payload;
    },
    fetchRestrictedData: (state) => {
      state.restrictedDataLoading = true;
      state.restrictedDataError = null;
    },
    setTilesDetails: (state, { payload }) => {
      state.tilesDetails = payload;
      state.restrictedDataLoading = false;
    },
    setOpenAiTokens: (state, { payload }) => {
      state.openAiTokens = payload;
    },
    fetchFacebookSuggestions: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
    },
    setImageUrl: (state, { payload }) => {
      state.imageUrl = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
    },
    fetchImage: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
    },
    setCopiedPost: (state, { payload }) => {
      console.log("Reducer: setCopiedPost triggered with payload:", payload);
      state.copiedPost = payload;
    },
    setVideoTaskId: (state, { payload }) => {
      state.videoTaskId = payload;
      state.suggestionsLoading = true;
    },
    setVideoUrl: (state, { payload }) => {
      state.videoUrl = payload;
      state.suggestionsLoading = false;
      state.suggestionsError = null;
    },
    // Timer wideo – identyczny wzorzec jak dla muzycznego
    startVideoProgressTimer: (state) => {
      state.videoTimerActive = true;
      state.videoTimerCompleted = false;
      state.videoProgress = 0;
    },
    updateVideoTimerProgress: (state, { payload }) => {
      state.videoProgress = payload;
    },
    completeVideoTimer: (state) => {
      state.videoTimerActive = false;
      state.videoTimerCompleted = true;
      state.videoProgress = 100;
    },
    resetVideoTimer: (state) => {
      state.videoTimerActive = false;
      state.videoTimerCompleted = false;
      state.videoProgress = null;
    },
    fetchVideo: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
      state.videoUrl = null;
      state.videoTaskId = null;
    },
    fetchAdvertisementSuggestions: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
    },
    fetchLastDietPlan: (state) => {
      state.suggestionsLoading = true;
      state.suggestionsError = null;
    },
    setLastDietPlan: (state, { payload }) => {
      if (payload) {
        state.data = payload.suggestions;
        state.dietPlanContext = payload.userInput;
        state.responseType = "suggestions";
      }
      state.suggestionsLoading = false;
    },
    setMusicTaskId: (state, { payload }) => {
      state.musicTaskId = payload;
      state.musicChatbotId = state.chatbotId;
      state.musicLoading = true;
      state.musicError = null;
      state.musicResult = null; // Reset previous result
    },
    setMusicUrl: (state, { payload }) => {
      state.musicUrl = payload;
      state.musicLoading = false;
      state.musicError = null;
    },
    // DODANE: Nowy reducer dla kompletnych danych muzyki
  setMusicResult: (state, { payload }) => {
  state.musicResult = payload;
  // Backward compatibility - użyj pierwszego utworu
  if (payload.tracks && payload.tracks.length > 0) {
    state.musicUrl = payload.tracks[0].audioUrl;
  } else {
    state.musicUrl = payload.audioUrl;
  }
  state.musicLoading = false;
  state.musicError = null;
  state.musicProgress = 100;
},
    setMusicError: (state, { payload }) => {
      state.musicLoading = false;
      state.musicError = payload;
      state.musicResult = null;
    },
    setMusicProgress: (state, { payload }) => {
      state.musicProgress = payload;
    },
    resetMusicState: (state) => {
      state.musicTaskId = null;
      state.musicUrl = null;
      state.musicLoading = false;
      state.musicError = null;
      state.musicTitle = null;
      state.musicProgress = null;
      state.musicResult = null; // Reset new field
      state.musicChatbotId = null;
    },
    fetchMusic: (state) => {
  state.musicLoading = true;
  state.musicError = null;
  state.musicResult = null;
},
startMusicProgressTimer: (state) => {
  state.musicTimerActive = true;
  state.musicTimerCompleted = false;
  state.musicProgress = 0;
},

updateMusicTimerProgress: (state, { payload }) => {
  state.musicProgress = payload;
},

completeMusicTimer: (state) => {
  state.musicTimerActive = false;
  state.musicTimerCompleted = true;
  state.musicProgress = 100;
},

resetMusicTimer: (state) => {
  state.musicTimerActive = false;
  state.musicTimerCompleted = false;
  state.musicProgress = null;
},
    setLoadingSuccess: (state) => { state.isLoading = "success";  },
    fetchUserData: (state) => {
      // Ta akcja będzie tylko wyzwalać sagę
    },
    setUserData: (state, { payload }) => {
      if (payload) {
        state.premiumGranted = payload.premiumGranted || false;
        state.generationCounts = payload.generationCounts || null;
        state.openAiTokens = payload.openAiTokens || 0; // DODANA LINIA
      }
    },
  },
});

export const {
  fetchData,
  setData,
  fetchSuggestions,
  setSuggestions,
  setContentSuggestions,
  setCommentSuggestions,
  setThemedSuggestions,
  clearSuggestions,
  setLegalAdvice,
   setGeneratedDocument,
  setError,
  changeThemeAfterDelay,
  togglePolishMode,
  toggleBodyOverflow,
  toggleBodyOverflowToFalse,
  setRouteName,
  setTimeOfDay,
  fetchSuggestionDetails,
  setSuggestionDetails,
  setDocumentError,
  clearSuggestionDetails,
  resetState,
  setChatbotId,
  saveChatHistory,
  setChatHistory,
  updateChatHistoryEntry,
  videoGenerationCompleted,
  incrementChatHistoryVersion,
  setUser,
  fetchRestrictedData,
  setTilesDetails,
  fetchFacebookSuggestions,
  setImageUrl,
  fetchImage,
  setCopiedPost,
  setVideoTaskId,
  setVideoUrl,
  fetchVideo,
  startVideoProgressTimer,
  updateVideoTimerProgress,
  completeVideoTimer,
  resetVideoTimer,
  fetchAdvertisementSuggestions,
  setLoadingSuccess,
  fetchLastDietPlan,
  setLastDietPlan,
  setMusicTaskId,
  setMusicUrl,
  setMusicResult, // DODANE: Export nowego action
  setMusicError,
  setMusicProgress,
  resetMusicState,
  fetchMusic,
   startMusicProgressTimer,
  updateMusicTimerProgress,
  completeMusicTimer,
  resetMusicTimer,
  musicChatbotId,
  setOpenAiTokens,
  fetchActiveSubscription, // Eksport nowej akcji
  setActiveSubscription, // Eksport nowej akcji
  fetchUserData,
  setUserData,
    startDocumentGeneration,
} = dataSlice.actions;

export const selectData = (state) => state.data.data || [];
export const selectContentSuggestions = (state) => state.data.contentSuggestions || [];
export const selectCommentSuggestions = (state) => state.data.commentSuggestions || [];
export const selectThemedSuggestions = (state) => state.data.themedSuggestions || [];
export const selectContentResponseType = (state) => state.data.contentResponseType;
export const selectCommentResponseType = (state) => state.data.commentResponseType;
export const selectThemedResponseType = (state) => state.data.themedResponseType;
export const selectImageUrl = (state) => state.data.imageUrl;
export const selectIsLoading = (state) => state.data.isLoading;
export const selectIsLightMode = (state) => state.data.lightMode;
export const selectIsPolishMode = (state) => state.data.polishMode;
export const selectIsBodyOverflowHidden = (state) => state.data.isBodyOverflowHidden;
export const selectRouteName = (state) => state.data.routeName;
export const selectIsNoon = (state) => state.data.isNoon;
export const selectSelectedSuggestion = (state) => state.data.selectedSuggestion;
export const selectSuggestionDetails = (state) => state.data.suggestionDetails;
export const selectDetailsLoading = (state) => state.data.detailsLoading;
export const selectResponseType = (state) => state.data.responseType;
export const selectSuggestionsLoading = (state) => state.data.suggestionsLoading;
export const selectSuggestionsError = (state) => state.data.suggestionsError;
export const selectDocumentGenerationError = (state) => state.data.documentGenerationError;
export const selectVideoFileId = (state) => state.data.videoFileId;
export const selectChatbotId = (state) => state.data.chatbotId;
export const selectChatHistory = (state) => state.data.chatHistory;
export const selectChatHistoryVersion = (state) => state.data.chatHistoryVersion;
export const selectUser = (state) => state.data.user;
export const selectTilesDetails = (state) => state.data.tilesDetails;
export const selectRestrictedDataLoading = (state) => state.data.restrictedDataLoading;
export const selectRestrictedDataError = (state) => state.data.restrictedDataError;
export const selectCopiedPost = (state) => state.data.copiedPost;
export const selectVideoTaskId = (state) => state.data.videoTaskId;
export const selectVideoUrl = (state) => state.data.videoUrl;
export const selectVideoProgress = (state) => state.data.videoProgress;
export const selectVideoTimerActive = (state) => state.data.videoTimerActive;
export const selectVideoTimerCompleted = (state) => state.data.videoTimerCompleted;
export const selectOpenAiTokens = (state) => state.data.openAiTokens; // Nowy selector
export const selectMusicTaskId = (state) => state.data.musicTaskId;
export const selectMusicUrl = (state) => state.data.musicUrl;
export const selectMusicLoading = (state) => state.data.musicLoading;
export const selectMusicError = (state) => state.data.musicError;
export const selectMusicProgress = (state) => state.data.musicProgress;
// DODANE: Nowy selector dla kompletnych danych muzyki
export const selectMusicResult = (state) => state.data.musicResult;
export const selectMusicTimerActive = (state) => state.data.musicTimerActive;
export const selectMusicTimerCompleted = (state) => state.data.musicTimerCompleted;
export const selectMusicChatbotId = (state) => state.data.musicChatbotId;
export const selectDietPlanContext = (state) => state.data.dietPlanContext;
export const selectActiveSubscription = (state) => state.data.activeSubscription; // Nowy selektor
export const selectPremiumGranted = (state) => state.data.premiumGranted;
export const selectGenerationCounts = (state) => state.data.generationCounts;

export default dataSlice.reducer;